import { openDB, type IDBPDatabase } from 'idb'

// Backend-agnostic persisted key/value cache. Currently IndexedDB (works in
// every WebView + browser, no native plugin). When offline mode lands this
// interface is the swap point for SQLite, keeping callers unchanged.
export type PersistedCache<T> = {
  load: () => Promise<T>
  save: (value: T) => Promise<void>
}

const DB_NAME = 'atsume'
const STORE_NAME = 'kv'

let dbPromise: Promise<IDBPDatabase> | null = null

function toPlainSnapshot<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T)
}

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME)
        }
      },
    })
  }
  return dbPromise
}

export function createPersistedCache<T>(key: string, defaultValue: T): PersistedCache<T> {
  return {
    async load() {
      try {
        const db = await getDb()
        const stored = await db.get(STORE_NAME, key)
        return (stored as T | undefined) ?? defaultValue
      } catch {
        return defaultValue
      }
    },
    async save(value: T) {
      const db = await getDb()
      await db.put(STORE_NAME, toPlainSnapshot(value), key)
    },
  }
}
